import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question: "I'm a grandparent. Can I call on behalf of my adult child?",
    answer:
      "Yes. You can call Neuro Wellness to ask questions without giving anyone's name. You can't schedule treatment for your adult child — that has to be their decision — but you can learn what's possible so you're ready when they are. The first appointment is an evaluation, not a commitment.",
  },
  {
    question: "If my child gets treated, will my grandchildren improve too?",
    answer:
      "Research following families over time shows that when a depressed parent gets effective treatment and reaches remission, their children's symptoms often improve, even without direct treatment of the child. A parent's recovery changes the emotional weather of the household. The most powerful thing that could happen for your grandchildren may be for their parent to get well.",
  },
  {
    question: "What if I'm depressed too?",
    answer:
      "Depression runs in families, and if your child has it, there's a fair chance you've carried it yourself. The same SAINT treatment that could help your child is cleared for adults 18 and older. You're allowed to get well too, and the same consultation covers both situations.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "How to Help Your Adult Child Through Depression Without Taking Over",
    href: "/help-your-adult-child-through-depression-without-taking-over",
    description:
      "What works, what doesn't, and how to be the person who knows what's available.",
  },
  {
    title:
      "What Exists Now That Didn't Exist When You Were Raising Children: A Grandparent's Guide",
    href: "/grandparents-guide-to-modern-depression-treatment",
    description:
      "How depression treatment has changed and what the options actually look like today.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function GrandparentWatchingFamilyStruggle() {
  return (
    <DepressionContentPage
      seo={{
        title: "Grandparent Watching Your Family Struggle: You're Not Helpless",
        description:
          "You're a grandparent watching your adult child and grandchildren struggle with depression. You're not helpless. What you're seeing, why it loops, and what exists now that didn't exist when you were raising children.",
        canonical:
          "/grandparent-watching-your-family-struggle-youre-not-helpless",
        keywords:
          "grandparent depression, adult child depressed, family depression, treatment-resistant depression family, SAINT TMS grandparent, help depressed adult child, depression grandchildren",
      }}
      badge="For Grandparents"
      heroTitle="You're a Grandparent Watching Your Child and Grandchildren Struggle, and You Feel Helpless. You're Not."
      heroSubtitle="You raised your child. You watched them build a life, a marriage, a family. And now you're watching that life come apart, slowly, from the outside. You've tried to help. And you've arrived at the worst place a grandparent can be: seeing clearly, loving completely, and not knowing what to do."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                You raised your child. You watched them build a life, a
                marriage, a family. And now you're watching that life come
                apart, slowly, from the outside.
              </p>
              <p>
                Maybe something happened: a death, a divorce, an illness, a loss
                that landed on the whole family at once. Maybe nothing happened,
                and the depression just arrived, the way it did in your own
                parents or your siblings, the way it may have arrived in you.
                Either way, you're watching your adult child go flat. Miss
                calls. Cancel visits. Snap at the kids and then go silent. And
                you're watching your grandchildren absorb it: the withdrawn
                teenager, the little one who's started acting out, the household
                that used to have laughter in it and now has a low hum of
                something you can't name.
              </p>
              <p>
                You've tried to help. You've offered, you've asked, you've maybe
                pushed too hard once and been told to back off. And you've
                arrived at the worst place a grandparent can be: seeing clearly,
                loving completely, and not knowing what to do.
              </p>
              <p>
                This is the first of three articles for you. This one is about
                understanding what you're seeing. The next two are about what to
                say and what actually exists now that didn't exist when you were
                raising children.
              </p>
            </div>
          ),
        },
        {
          heading: "What you're seeing has a name",
          body: (
            <div className="space-y-5">
              <p>
                When a person, adult or child, is caught in a persistent low
                that doesn't lift, loses interest in the things they loved,
                can't sleep or sleeps too much, can't concentrate, and seems
                stuck in the same worry or the same grief on a loop, that is not
                a bad stretch. It's very likely major depressive disorder. When
                it comes with racing thoughts, dread, and irritability, anxiety
                is riding along with it.
              </p>
              <p>
                And when your adult child has already seen a doctor, tried a
                medication or two, and is still like this, there's a more
                specific name: treatment-resistant depression. It means the
                standard first tools didn't work. It does not mean nothing will.
              </p>
            </div>
          ),
        },
        {
          heading: "Why it looks like a loop",
          body: (
            <div className="space-y-5">
              <p>
                You've probably used the word "stuck." That's accurate.
                Depression suppresses the region of the brain behind the
                forehead that regulates mood, motivation, and clear thinking.
                That's the same region a person would use to decide to get help,
                to break a pattern, to see a way out. Anxiety floods that region
                with alarm. The result is a mind that circles the same ground
                because the part that could step off the track is the part
                that's impaired.
              </p>
              <p>
                That's why "just snap out of it" has never worked, and why your
                child may seem unable to take even the small steps you've
                suggested. They're not refusing. The apparatus for taking steps
                is what's broken.
              </p>
            </div>
          ),
        },
        {
          heading: "Why the grandchildren matter in this",
          body: (
            <div className="space-y-5">
              <p>
                Children of a depressed parent are at significantly higher risk
                of depression and anxiety themselves. They also feel the
                household change long before anyone names it. Your
                grandchildren's moodiness, withdrawal, or acting out may be
                their own reaction to what's happening in the house.
              </p>
              <p>
                Here's the hopeful part of that. Research following families
                over time shows that when a depressed parent gets effective
                treatment and reaches remission, their children's symptoms
                improve, often without the children being treated directly. The
                most powerful thing that could happen for your grandchildren is
                for their parent to get well.
              </p>
              <p>
                That reframes your helplessness. You may not be able to fix
                three people. You may only need to help one.
              </p>
            </div>
          ),
        },
        {
          heading: "What helplessness is actually telling you",
          body: (
            <div className="space-y-5">
              <p>
                The feeling that you don't know what to do is real, but it isn't
                accurate. It's the feeling of not knowing what to do yet. Most
                grandparents in this position don't have a map of what treatment
                for depression looks like now. The last time they thought
                seriously about it, the options were talk therapy, a pill, and,
                in severe cases, a hospital. That map is thirty years out of
                date.
              </p>
              <p>
                What exists now is different. There are treatments that work on
                the brain's circuitry directly, that don't require months, that
                don't add another medication to the pile, and that produce full
                remission in most people who had already failed everything else.
                Understanding that is the first step out of helpless.
              </p>
            </div>
          ),
        },
        {
          heading: "What you can and can't do",
          body: (
            <div className="space-y-5">
              <p>
                You can't make your adult child accept help. You can't diagnose
                them. You can't, and shouldn't, go around them to their doctor
                or their spouse.
              </p>
              <p>
                You can see clearly. You can say something, once, carefully. You
                can learn what's available so that when they're ready, you're
                the person who knows. You can offer the things grandparents are
                uniquely positioned to offer: time, presence with the
                grandchildren, and sometimes the resources to remove a practical
                obstacle.
              </p>
              <p>The next two articles are about how.</p>
            </div>
          ),
        },
        {
          heading: "If you're depressed too",
          body: (
            <div className="space-y-5">
              <p>
                One more thing. Depression runs in families, and if your child
                has it, there's a fair chance you've carried it yourself,
                treated or not. Watching your family suffer is exactly the kind
                of prolonged stress that brings it back or brings it out. If you
                recognize yourself in any of this, the same treatments that
                could help your child could help you. You're allowed to get well
                too.
              </p>
            </div>
          ),
        },
        {
          heading: "Where to start",
          body: (
            <div className="space-y-4">
              <p>
                Neuro Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                offering an FDA-cleared, five-day treatment for adults whose
                depression hasn't responded to medication. You can call to ask
                questions on behalf of your family without giving anyone's name.
                You can't schedule treatment for your adult child, and you
                shouldn't try. But you can find out what's possible so you're
                ready when they are.
              </p>
              <p className="font-semibold text-primary">
                Ask questions or schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If anyone in your family talks about suicide or not wanting to
                be here, treat it as an emergency. Call or text 988 (Suicide
                &amp; Crisis Lifeline), or call it yourself for guidance. This
                article is for educational purposes and is not a substitute for
                individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="You're not helpless. You're the person who can learn what's possible and be ready when they are."
    />
  );
}
